import React, { useState, useRef, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Mail, Phone, ArrowRight, ArrowLeft, Shield, Lock, ChevronDown, Search } from 'lucide-react';
import { trackFormFill } from '../utils/analytics';

// 70+ countries — code + name (no flag emoji for cross-platform consistency)
const COUNTRY_CODES = [
  // South Asia (most relevant for MU)
  { code: '+91',  name: 'India' },
  { code: '+92',  name: 'Pakistan' },
  { code: '+880', name: 'Bangladesh' },
  { code: '+94',  name: 'Sri Lanka' },
  { code: '+977', name: 'Nepal' },
  { code: '+975', name: 'Bhutan' },
  { code: '+960', name: 'Maldives' },
  // Middle East
  { code: '+971', name: 'UAE' },
  { code: '+966', name: 'Saudi Arabia' },
  { code: '+974', name: 'Qatar' },
  { code: '+965', name: 'Kuwait' },
  { code: '+973', name: 'Bahrain' },
  { code: '+968', name: 'Oman' },
  { code: '+972', name: 'Israel' },
  { code: '+90',  name: 'Turkey' },
  // East & SE Asia
  { code: '+65',  name: 'Singapore' },
  { code: '+60',  name: 'Malaysia' },
  { code: '+62',  name: 'Indonesia' },
  { code: '+63',  name: 'Philippines' },
  { code: '+66',  name: 'Thailand' },
  { code: '+84',  name: 'Vietnam' },
  { code: '+86',  name: 'China' },
  { code: '+81',  name: 'Japan' },
  { code: '+82',  name: 'South Korea' },
  { code: '+852', name: 'Hong Kong' },
  { code: '+886', name: 'Taiwan' },
  { code: '+95',  name: 'Myanmar' },
  { code: '+855', name: 'Cambodia' },
  // North America
  { code: '+1',   name: 'USA / Canada' },
  { code: '+52',  name: 'Mexico' },
  // Europe
  { code: '+44',  name: 'United Kingdom' },
  { code: '+49',  name: 'Germany' },
  { code: '+33',  name: 'France' },
  { code: '+31',  name: 'Netherlands' },
  { code: '+41',  name: 'Switzerland' },
  { code: '+46',  name: 'Sweden' },
  { code: '+47',  name: 'Norway' },
  { code: '+45',  name: 'Denmark' },
  { code: '+358', name: 'Finland' },
  { code: '+32',  name: 'Belgium' },
  { code: '+43',  name: 'Austria' },
  { code: '+34',  name: 'Spain' },
  { code: '+39',  name: 'Italy' },
  { code: '+351', name: 'Portugal' },
  { code: '+353', name: 'Ireland' },
  { code: '+48',  name: 'Poland' },
  { code: '+420', name: 'Czech Republic' },
  { code: '+7',   name: 'Russia' },
  { code: '+380', name: 'Ukraine' },
  { code: '+30',  name: 'Greece' },
  // South America
  { code: '+55',  name: 'Brazil' },
  { code: '+54',  name: 'Argentina' },
  { code: '+56',  name: 'Chile' },
  { code: '+57',  name: 'Colombia' },
  { code: '+51',  name: 'Peru' },
  // Africa
  { code: '+27',  name: 'South Africa' },
  { code: '+234', name: 'Nigeria' },
  { code: '+254', name: 'Kenya' },
  { code: '+20',  name: 'Egypt' },
  { code: '+233', name: 'Ghana' },
  { code: '+251', name: 'Ethiopia' },
  { code: '+255', name: 'Tanzania' },
  // Oceania
  { code: '+61',  name: 'Australia' },
  { code: '+64',  name: 'New Zealand' },
];

export const ContactForm = ({ onSubmit, onBack, isLoading }) => {
  const [email,        setEmail]        = useState('');
  const [countryCode,  setCountryCode]  = useState('+91');
  const [phoneDigits,  setPhoneDigits]  = useState('');
  const [currentCtc,   setCurrentCtc]   = useState('');
  const [isStudent,    setIsStudent]    = useState(false);
  const [errors,       setErrors]       = useState({});
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [ccSearch,     setCcSearch]     = useState('');
  const dropdownRef = useRef(null);

  // Close dropdown on outside click
  useEffect(() => {
    if (!dropdownOpen) return;
    const handler = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setDropdownOpen(false); setCcSearch('');
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [dropdownOpen]);

  const validateEmail = (val) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(val);
  // Always require exactly 10 digits after the country code
  const validatePhone = (digits) => /^\d{10}$/.test(digits.trim());

  const validateCtc = (val) => {
    if (!val && val !== 0) return false;
    const n = Number(val);
    return Number.isInteger(n) && n >= 1 && n <= 200;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const newErrors = {};
    if (!email.trim()) newErrors.email = 'Email is required';
    else if (!validateEmail(email)) newErrors.email = 'Please enter a valid email';
    if (!phoneDigits.trim()) newErrors.phone = 'Phone number is required';
    else if (!validatePhone(phoneDigits)) newErrors.phone = 'Please enter a 10-digit phone number';
    if (!isStudent) {
      if (!currentCtc && currentCtc !== 0) {
        newErrors.ctc = 'Please enter your CTC in LPA or select "I am a student"';
      } else if (!validateCtc(currentCtc)) {
        newErrors.ctc = 'Please enter a whole number between 1 and 200 (in LPA)';
      }
    }
    if (Object.keys(newErrors).length > 0) { setErrors(newErrors); return; }

    trackFormFill({ email: email.trim(), phone: `${countryCode}${phoneDigits}`, current_ctc: String(currentCtc) });

    onSubmit({
      email:       email.trim(),
      phone:       `${countryCode} ${phoneDigits}`,   // single field: "+91 9876543210"
      current_ctc: isStudent ? 'Student' : `${currentCtc} LPA`,
    });
  };

  const inputStyle = (hasError) => ({
    width: '100%',
    backgroundColor: '#fafafa',
    border: `1.5px solid ${hasError ? 'rgba(217,64,64,0.50)' : 'rgba(0,0,0,0.12)'}`,
    borderRadius: '10px',
    padding: '12px 14px 12px 42px',
    fontFamily: 'DM Sans, sans-serif',
    fontSize: '14.5px',
    color: '#111111',
    outline: 'none',
    transition: 'border-color 0.2s ease, box-shadow 0.2s ease',
  });

  const labelStyle = {
    display: 'block',
    fontFamily: 'DM Sans, sans-serif',
    fontSize: '14px',
    fontWeight: 600,
    color: 'rgba(0,0,0,0.50)',
    marginBottom: '6px',
    letterSpacing: '0.06em',
    textTransform: 'uppercase',
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -12 }}
      transition={{ duration: 0.25 }}
      data-testid="contact-form"
    >
      {/* Heading */}
      <div style={{ marginBottom: '22px' }}>
        <h3 style={{
          fontFamily: 'DM Sans, sans-serif',
          fontSize: '20px', fontWeight: 700, color: '#111111', marginBottom: '6px',
        }}>
          Your report is almost ready!
        </h3>
        <p style={{
          fontFamily: 'DM Sans, sans-serif',
          fontSize: '14.5px', color: 'rgba(0,0,0,0.48)', lineHeight: '1.5',
        }}>
          Add your details to unlock your career analysis report.
        </p>
      </div>

      <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
        {/* Email */}
        <div>
          <label style={labelStyle}>Email Address</label>
          <div style={{ position: 'relative' }}>
            <Mail size={15} color="rgba(0,0,0,0.30)" style={{ position: 'absolute', left: '14px', top: '50%', transform: 'translateY(-50%)' }} />
            <input
              data-testid="contact-email-input"
              type="email" value={email}
              onChange={(e) => { setEmail(e.target.value); if (errors.email) setErrors(p => ({ ...p, email: '' })); }}
              placeholder="your@email.com"
              style={inputStyle(errors.email)}
              onFocus={e => { e.target.style.borderColor = '#E8A020'; e.target.style.boxShadow = '0 0 0 3px rgba(232,160,32,0.10)'; }}
              onBlur={e => { e.target.style.borderColor = errors.email ? 'rgba(217,64,64,0.50)' : 'rgba(0,0,0,0.12)'; e.target.style.boxShadow = 'none'; }}
            />
          </div>
          {errors.email && <p style={{ fontFamily: 'DM Sans', fontSize: '14.5px', color: '#D94040', marginTop: '5px' }}>{errors.email}</p>}
        </div>

        {/* Phone */}
        <div>
          <label style={labelStyle}>Phone Number</label>

          {/* Phone row — ref wraps everything so click-outside works,
               dropdown panel is a SIBLING of the overflow:hidden input row
               so it's never clipped */}
          <div ref={dropdownRef} style={{ position: 'relative' }}>

            {/* Input row */}
            <div style={{
              display: 'flex', alignItems: 'stretch',
              border: `1.5px solid ${errors.phone ? 'rgba(217,64,64,0.50)' : 'rgba(0,0,0,0.12)'}`,
              borderRadius: '10px',
              backgroundColor: '#fafafa',
              overflow: 'hidden',          /* rounds corners on inner elements */
              transition: 'border-color 0.2s ease, box-shadow 0.2s ease',
            }}
              onFocusCapture={e => {
                e.currentTarget.style.borderColor = '#E8A020';
                e.currentTarget.style.boxShadow = '0 0 0 3px rgba(232,160,32,0.10)';
              }}
              onBlurCapture={e => {
                e.currentTarget.style.borderColor = errors.phone ? 'rgba(217,64,64,0.50)' : 'rgba(0,0,0,0.12)';
                e.currentTarget.style.boxShadow = 'none';
              }}
            >
              {/* Phone icon */}
              <div style={{ display: 'flex', alignItems: 'center', paddingLeft: '14px', paddingRight: '8px', flexShrink: 0 }}>
                <Phone size={15} color="rgba(0,0,0,0.30)" />
              </div>

              {/* Country code trigger — inside overflow:hidden row but dropdown opens outside */}
              <button
                type="button"
                onClick={() => { setDropdownOpen(o => !o); setCcSearch(''); }}
                style={{
                  display: 'flex', alignItems: 'center', gap: '4px',
                  height: '100%', padding: '12px 8px 12px 4px',
                  border: 'none',
                  borderRight: '1px solid rgba(0,0,0,0.10)',
                  backgroundColor: '#f0f0f0',
                  cursor: 'pointer',
                  fontFamily: 'DM Sans, sans-serif',
                  fontSize: '14px', fontWeight: 600, color: '#111111',
                  whiteSpace: 'nowrap', flexShrink: 0,
                  transition: 'background-color 0.15s ease',
                  minWidth: '64px',
                }}
                onMouseEnter={e => e.currentTarget.style.backgroundColor = '#e6e6e6'}
                onMouseLeave={e => e.currentTarget.style.backgroundColor = '#f0f0f0'}
              >
                {countryCode}
                <ChevronDown size={12} color="rgba(0,0,0,0.45)"
                  style={{ transform: dropdownOpen ? 'rotate(180deg)' : 'none', transition: 'transform 0.2s ease', flexShrink: 0 }}
                />
              </button>

              {/* 10-digit input */}
              <input
                data-testid="contact-phone-input"
                type="tel"
                inputMode="numeric"
                maxLength={10}
                value={phoneDigits}
                onChange={e => {
                  const digits = e.target.value.replace(/\D/g, '').slice(0, 10);
                  setPhoneDigits(digits);
                  if (errors.phone) setErrors(p => ({ ...p, phone: '' }));
                }}
                placeholder="10-digit number"
                style={{
                  flex: 1, border: 'none', backgroundColor: 'transparent',
                  fontFamily: 'DM Sans, sans-serif', fontSize: '14px', color: '#111111',
                  padding: '12px 10px', outline: 'none', minWidth: 0,
                }}
                onWheel={e => e.target.blur()}
              />

              {/* Live digit counter */}
              <div style={{ display: 'flex', alignItems: 'center', paddingRight: '12px', flexShrink: 0 }}>
                <span style={{
                  fontFamily: 'DM Sans, sans-serif', fontSize: '11px', fontWeight: 600,
                  color: phoneDigits.length === 10 ? '#3FA86A' : 'rgba(0,0,0,0.28)',
                  transition: 'color 0.2s ease',
                }}>
                  {phoneDigits.length}/10
                </span>
              </div>
            </div>

            {/* ── Dropdown panel — OUTSIDE the overflow:hidden row, never clipped ── */}
            {dropdownOpen && (
              <div style={{
                position: 'absolute',
                top: 'calc(100% + 6px)', left: 0,
                width: '240px', maxHeight: '260px',
                backgroundColor: '#ffffff',
                border: '1.5px solid rgba(0,0,0,0.12)',
                borderRadius: '10px',
                boxShadow: '0 8px 24px rgba(0,0,0,0.14)',
                zIndex: 200,
                display: 'flex', flexDirection: 'column',
                overflow: 'hidden',
              }}>
                {/* Search */}
                <div style={{ padding: '9px 11px', borderBottom: '1px solid rgba(0,0,0,0.07)', display: 'flex', alignItems: 'center', gap: '7px', flexShrink: 0 }}>
                  <Search size={13} color="rgba(0,0,0,0.35)" style={{ flexShrink: 0 }} />
                  <input
                    autoFocus
                    type="text"
                    value={ccSearch}
                    onChange={e => setCcSearch(e.target.value)}
                    placeholder="Search country..."
                    style={{ border: 'none', outline: 'none', fontFamily: 'DM Sans', fontSize: '13px', color: '#111', backgroundColor: 'transparent', flex: 1, width: '100%' }}
                  />
                </div>

                {/* Options */}
                <div style={{ overflowY: 'auto', flex: 1 }}>
                  {COUNTRY_CODES
                    .filter(c =>
                      c.name.toLowerCase().includes(ccSearch.toLowerCase()) ||
                      c.code.includes(ccSearch)
                    )
                    .map(c => (
                      <button
                        key={c.code + c.name}
                        type="button"
                        onClick={() => {
                          setCountryCode(c.code);
                          setDropdownOpen(false); setCcSearch('');
                          if (errors.phone) setErrors(p => ({ ...p, phone: '' }));
                        }}
                        style={{
                          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                          width: '100%', padding: '9px 13px',
                          border: 'none', textAlign: 'left',
                          backgroundColor: c.code === countryCode && c.name === COUNTRY_CODES.find(x=>x.code===countryCode)?.name
                            ? 'rgba(232,160,32,0.08)' : 'transparent',
                          cursor: 'pointer', gap: '10px',
                          transition: 'background-color 0.1s ease',
                        }}
                        onMouseEnter={e => { e.currentTarget.style.backgroundColor = '#f5f5f5'; }}
                        onMouseLeave={e => {
                          e.currentTarget.style.backgroundColor = c.code === countryCode
                            ? 'rgba(232,160,32,0.08)' : 'transparent';
                        }}
                      >
                        <span style={{ fontFamily: 'DM Sans', fontSize: '13px', color: '#111', flex: 1 }}>{c.name}</span>
                        <span style={{ fontFamily: 'DM Sans', fontSize: '12px', fontWeight: 600, color: c.code === countryCode ? '#C47A00' : 'rgba(0,0,0,0.40)', flexShrink: 0 }}>{c.code}</span>
                      </button>
                    ))
                  }
                </div>
              </div>
            )}
          </div>
          {errors.phone && <p style={{ fontFamily: 'DM Sans', fontSize: '12px', color: '#D94040', marginTop: '5px' }}>{errors.phone}</p>}
        </div>

        {/* Current CTC / Founder's Compensation — mandatory unless student */}
        <div>
          <label style={labelStyle}>
            Current CTC / Founder's Compensation
            <span style={{
              marginLeft: '6px', color: '#D94040',
              textTransform: 'none', letterSpacing: 0, fontWeight: 700,
            }}>*</span>
            <span style={{
              marginLeft: '4px', color: 'rgba(0,0,0,0.35)',
              textTransform: 'none', letterSpacing: 0, fontWeight: 400,
            }}>in LPA (₹)</span>
          </label>

          {/* Student toggle */}
          <label style={{
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            marginBottom: '8px',
            cursor: 'pointer',
          }}>
            <div
              onClick={() => {
                setIsStudent(!isStudent);
                if (!isStudent) { setCurrentCtc(''); if (errors.ctc) setErrors(p => ({ ...p, ctc: '' })); }
              }}
              style={{
                width: '18px', height: '18px', borderRadius: '4px', flexShrink: 0,
                backgroundColor: isStudent ? '#E8A020' : '#ffffff',
                border: `1.5px solid ${isStudent ? '#E8A020' : 'rgba(0,0,0,0.20)'}`,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                transition: 'background-color 0.2s ease, border-color 0.2s ease',
                cursor: 'pointer',
              }}
            >
              {isStudent && (
                <svg width="10" height="10" viewBox="0 0 10 10" fill="none">
                  <path d="M2 5l2.5 2.5L8 2.5" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              )}
            </div>
            <span style={{
              fontFamily: 'DM Sans, sans-serif',
              fontSize: '14px',
              color: isStudent ? '#111111' : 'rgba(0,0,0,0.55)',
              fontWeight: isStudent ? 500 : 400,
              userSelect: 'none',
            }}>
              I am a student
            </span>
          </label>

          {/* CTC input — hidden when student */}
          {!isStudent && (
            <div style={{ position: 'relative' }}>
              {/* ₹ icon as text */}
              <span style={{
                position: 'absolute', left: '14px', top: '50%', transform: 'translateY(-50%)',
                fontFamily: 'DM Sans, sans-serif', fontSize: '14.5px',
                color: 'rgba(0,0,0,0.35)', fontWeight: 500, lineHeight: 1,
                pointerEvents: 'none',
              }}>₹</span>
              <input
                data-testid="contact-ctc-input"
                type="number"
                min="1"
                max="200"
                step="1"
                value={currentCtc}
                onChange={(e) => {
                  // Only allow positive integers — strip decimals and negatives
                  const val = e.target.value;
                  if (val === '' || (/^\d+$/.test(val) && parseInt(val) <= 200)) {
                    setCurrentCtc(val === '' ? '' : parseInt(val, 10));
                    if (errors.ctc) setErrors(p => ({ ...p, ctc: '' }));
                  }
                }}
                placeholder="e.g. 12"
                style={{
                  ...inputStyle(errors.ctc),
                  /* Hide browser spinner arrows — keep input clean */
                  MozAppearance: 'textfield',
                  WebkitAppearance: 'none',
                }}
                onFocus={e => { e.target.style.borderColor = '#E8A020'; e.target.style.boxShadow = '0 0 0 3px rgba(232,160,32,0.10)'; }}
                onBlur={e => { e.target.style.borderColor = errors.ctc ? 'rgba(217,64,64,0.50)' : 'rgba(0,0,0,0.12)'; e.target.style.boxShadow = 'none'; }}
                /* Prevent scroll from changing the value accidentally on mobile */
                onWheel={e => e.target.blur()}
              />
            </div>
          )}
          {errors.ctc && <p style={{ fontFamily: 'DM Sans', fontSize: '14.5px', color: '#D94040', marginTop: '5px' }}>{errors.ctc}</p>}
        </div>

        {/* Privacy */}
        <div style={{
          display: 'flex', alignItems: 'flex-start', gap: '8px',
          backgroundColor: '#f0faf5', border: '1px solid rgba(63,168,106,0.18)',
          borderRadius: '8px', padding: '10px 12px',
        }}>
          {/* Green filled shield icon */}
          <div style={{
            width: '20px', height: '20px', borderRadius: '50%', flexShrink: 0,
            backgroundColor: '#3FA86A',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            marginTop: '0px',
          }}>
            <Shield size={11} color="#ffffff" strokeWidth={2.5} />
          </div>
          <p style={{ fontFamily: 'DM Sans, sans-serif', fontSize: '14.5px', color: 'rgba(0,0,0,0.48)', lineHeight: '1.5' }}>
            Your resume and details are protected. We do not sell or share your data.
          </p>
        </div>

        {/* Buttons */}
        <div style={{ display: 'flex', gap: '10px', marginTop: '4px' }}>
          <button type="button" data-testid="wizard-back-button" onClick={onBack} disabled={isLoading}
            style={{
              display: 'flex', alignItems: 'center', gap: '6px',
              padding: '11px 18px', backgroundColor: '#f5f5f5',
              border: '1.5px solid rgba(0,0,0,0.10)', borderRadius: '9999px',
              cursor: 'pointer', fontFamily: 'DM Sans, sans-serif',
              fontSize: '14.5px', fontWeight: 500, color: 'rgba(0,0,0,0.55)',
              transition: 'border-color 0.2s ease, color 0.2s ease',
            }}
            onMouseEnter={e => { e.currentTarget.style.borderColor = 'rgba(0,0,0,0.22)'; e.currentTarget.style.color = '#111'; }}
            onMouseLeave={e => { e.currentTarget.style.borderColor = 'rgba(0,0,0,0.10)'; e.currentTarget.style.color = 'rgba(0,0,0,0.55)'; }}
          >
            <ArrowLeft size={14} /> Back
          </button>

          <button type="submit" data-testid="contact-submit-button" disabled={isLoading}
            style={{
              flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center',
              gap: '8px', padding: '11px 24px', backgroundColor: '#E8A020',
              border: 'none', borderRadius: '9999px',
              cursor: isLoading ? 'not-allowed' : 'pointer',
              fontFamily: 'DM Sans, sans-serif', fontSize: '14.5px', fontWeight: 700,
              color: '#ffffff', opacity: isLoading ? 0.7 : 1,
              transition: 'opacity 0.2s ease, transform 0.15s ease',
            }}
            onMouseEnter={e => !isLoading && (e.currentTarget.style.transform = 'scale(1.01)')}
            onMouseLeave={e => (e.currentTarget.style.transform = 'scale(1)')}
          >
            {isLoading ? (
              'Starting Analysis...'
            ) : (
              <><Lock size={14} /> Unlock My Report <ArrowRight size={14} /></>
            )}
          </button>
        </div>
      </form>
    </motion.div>
  );
};
